import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const ImplantCenterLayout = ({ children, title, description, keywords, schema }) => {
  const location = useLocation();
  
  // Map React routes to original WordPress subdomain URLs
  // /implant-center -> /
  // /implant-center/dental-implants -> /dental-implants/
  const getCanonicalPath = () => {
    const path = location.pathname.replace('/implant-center', '');
    return path === '' ? '/' : `${path}/`;
  };
  
  const canonicalUrl = `https://dentalimplants.fortworthdental.com${getCanonicalPath()}`;
  
  const navLinks = [
    { path: '/implant-center', label: 'Home' },
    { path: '/implant-center/dental-implants', label: 'Dental Implants' },
    { path: '/implant-center/mini-dental-implants', label: 'Mini Implants' },
    { path: '/implant-center/jawbone-grafting-and-sinus-lift', label: 'Jawbone Grafting' },
    { path: '/implant-center/cosmetic-dentistry-at-fort-worth-dental', label: 'Cosmetic Dentistry' },
    { path: '/implant-center/fort-worth-dental-provides-a-dental-plan-you-can-afford', label: 'Dental Plan' },
  ];

  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental Implant Center",
    "description": "Fort Worth's premier dental implant center offering affordable dental implants, mini implants, All-on-4, and implant-supported dentures with financing options.",
    "url": "https://dentalimplants.fortworthdental.com",
    "telephone": "+1-817-854-4329",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "32.6799",
      "longitude": "-97.4089"
    },
    "areaServed": [
      "Fort Worth", "Dallas", "Arlington", "Southlake", "Weatherford", 
      "Stephenville", "Benbrook", "Aledo", "DFW"
    ],
    "priceRange": "$$",
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Monday", "opens": "09:00", "closes": "17:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Tuesday", "opens": "09:00", "closes": "18:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Wednesday", "opens": "07:00", "closes": "17:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Thursday", "opens": "09:00", "closes": "18:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Friday", "opens": "07:00", "closes": "14:00" }
    ],
    "medicalSpecialty": ["Dental Implants", "Cosmetic Dentistry", "Implant Dentistry"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dental Implant Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Single Tooth Dental Implant" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "All-on-4 Dental Implants" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mini Dental Implants" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Implant-Supported Dentures" }}
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Fort Worth Dental Implant Center" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(schema || defaultSchema)}
        </script>
      </Helmet>

      {/* Implant Center Sub-Navigation */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <Link to="/implant-center" className="flex items-center space-x-2">
              <span className="text-xl font-bold">Implant Center</span>
            </Link>
            <a 
              href="tel:8178544329" 
              className="bg-white text-teal-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              FREE Consult: (817) 854-4329
            </a>
          </div>
          <nav className="flex flex-wrap gap-1 pb-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  location.pathname === link.path
                    ? 'bg-white text-teal-700'
                    : 'hover:bg-teal-600 text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Page Content */}
      {children}

      {/* Implant Center Footer CTA */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Restore Your Smile?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Schedule your FREE dental implant consultation with X-rays today. 
            Serving Fort Worth, Dallas, Arlington, and the entire DFW metroplex.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="tel:8178544329" 
              className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition"
            >
              Call (817) 854-4329
            </a>
            <Link 
              to="/implant-center#consultation-form" 
              className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-400 transition border-2 border-white"
            >
              Request FREE Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImplantCenterLayout;
